import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(2deg); }
`;

const HeroContainer = styled(Box)({
  minHeight: '100vh',
  background: '#0a0a0f',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden'
});

const BackgroundPattern = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `
    linear-gradient(135deg, rgba(212, 175, 55, 0.03) 0%, transparent 50%),
    linear-gradient(225deg, rgba(120, 119, 198, 0.05) 0%, transparent 50%)
  `,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      radial-gradient(circle at 2px 2px, rgba(255,255,255,0.03) 1px, transparent 0)
    `,
    backgroundSize: '40px 40px'
  }
});

const GoldGlow = styled(Box)({
  position: 'absolute',
  top: '20%',
  right: '10%',
  width: '600px',
  height: '600px',
  background: 'radial-gradient(circle, rgba(212, 175, 55, 0.15) 0%, transparent 60%)',
  filter: 'blur(80px)',
  pointerEvents: 'none',
  animation: `${pulse} 8s ease-in-out infinite`
});

const PurpleGlow = styled(Box)({
  position: 'absolute',
  bottom: '10%',
  left: '5%',
  width: '500px',
  height: '500px',
  background: 'radial-gradient(circle, rgba(120, 119, 198, 0.12) 0%, transparent 60%)',
  filter: 'blur(80px)',
  pointerEvents: 'none',
  animation: `${pulse} 10s ease-in-out infinite`,
  animationDelay: '2s'
});

const ContentWrapper = styled(Box)({
  width: '100%',
  maxWidth: '1500px',
  margin: '0 auto',
  padding: '0 80px',
  display: 'grid',
  gridTemplateColumns: '1.1fr 1fr',
  gap: '100px',
  alignItems: 'center',
  position: 'relative',
  zIndex: 10,
  '@media (max-width: 1200px)': {
    gridTemplateColumns: '1fr',
    padding: '140px 40px 80px',
    gap: '60px',
    textAlign: 'center'
  },
  '@media (max-width: 768px)': {
    padding: '120px 24px 60px'
  }
});

const TextContent = styled(Box)({
  animation: `${fadeInLeft} 1s ease-out`
});

const TopLabel = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '32px'
});

const LabelLine = styled(Box)({
  width: '40px',
  height: '2px',
  background: 'linear-gradient(90deg, #d4af37, transparent)'
});

const LabelText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 600,
  color: '#d4af37',
  letterSpacing: '3px',
  textTransform: 'uppercase'
});

const HeroTitle = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: 'clamp(44px, 6vw, 76px)',
  fontWeight: 400,
  color: '#ffffff',
  lineHeight: 1.1,
  marginBottom: '28px',
  letterSpacing: '-2px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 50%, #d4af37 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  animation: `${shimmer} 4s linear infinite`
});

const HeroSubtitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: 'clamp(16px, 1.8vw, 19px)',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.6)',
  lineHeight: 1.9,
  marginBottom: '40px',
  maxWidth: '520px',
  '@media (max-width: 1200px)': {
    margin: '0 auto 40px'
  }
});

const FeatureList = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  marginBottom: '48px',
  '@media (max-width: 1200px)': {
    justifyContent: 'center'
  }
});

const FeatureItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 18px',
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '50px',
  '& svg': {
    fontSize: '16px',
    color: '#d4af37'
  }
});

const FeatureText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.8)'
});

const ButtonGroup = styled(Box)({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  '@media (max-width: 1200px)': {
    justifyContent: 'center'
  },
  '@media (max-width: 480px)': {
    flexDirection: 'column'
  }
});

const PrimaryButton = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '12px',
  padding: '20px 40px',
  background: 'linear-gradient(135deg, #d4af37 0%, #c9a227 100%)',
  borderRadius: '60px',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.35)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    transition: 'left 0.5s ease'
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 50px rgba(212, 175, 55, 0.45)',
    '&::before': {
      left: '100%'
    },
    '& .btn-arrow': {
      transform: 'translateX(4px)'
    }
  }
});

const PrimaryButtonText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 600,
  color: '#0a0a0f',
  letterSpacing: '0.5px'
});

const ButtonArrow = styled(ArrowForwardIcon)({
  fontSize: '20px',
  color: '#0a0a0f',
  transition: 'transform 0.3s ease'
});

const SecondaryButton = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  padding: '20px 32px',
  background: 'transparent',
  border: '2px solid rgba(255, 255, 255, 0.15)',
  borderRadius: '60px',
  cursor: 'pointer',
  transition: 'all 0.4s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.05)',
    borderColor: 'rgba(212, 175, 55, 0.5)',
    '& svg': {
      color: '#d4af37'
    }
  }
});

const SecondaryButtonText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 500,
  color: '#ffffff'
});

const ImageSection = styled(Box)({
  position: 'relative',
  animation: `${fadeInRight} 1s ease-out 0.2s both`,
  '@media (max-width: 1200px)': {
    order: -1,
    maxWidth: '600px',
    margin: '0 auto'
  }
});

const ImageGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gridTemplateRows: 'auto auto',
  gap: '20px',
  position: 'relative'
});

const MainImage = styled(Box)({
  gridColumn: '1 / -1',
  height: '320px',
  borderRadius: '24px',
  overflow: 'hidden',
  position: 'relative',
  boxShadow: '0 30px 70px rgba(0, 0, 0, 0.5)',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.6s ease'
  },
  '&:hover img': {
    transform: 'scale(1.05)'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.4) 100%)'
  },
  '@media (max-width: 768px)': {
    height: '250px'
  }
});

const SmallImage = styled(Box)({
  height: '180px',
  borderRadius: '20px',
  overflow: 'hidden',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.6s ease'
  },
  '&:hover img': {
    transform: 'scale(1.05)'
  },
  '@media (max-width: 768px)': {
    height: '140px'
  }
});

const RatingCard = styled(Box)({
  position: 'absolute',
  top: '30px',
  left: '-30px',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: '20px',
  padding: '20px 28px',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
  animation: `${float} 6s ease-in-out infinite`,
  zIndex: 20,
  '@media (max-width: 768px)': {
    left: '10px',
    top: '20px',
    padding: '14px 20px'
  }
});

const RatingStars = styled(Box)({
  display: 'flex',
  gap: '4px',
  marginBottom: '8px',
  '& svg': {
    fontSize: '18px',
    color: '#d4af37'
  }
});

const RatingText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 600,
  color: '#1a1a2e'
});

const RatingSubtext = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 400,
  color: '#888888'
});

const ExperienceCard = styled(Box)({
  position: 'absolute',
  bottom: '100px',
  right: '-20px',
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  borderRadius: '20px',
  padding: '24px 32px',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)',
  border: '1px solid rgba(212, 175, 55, 0.2)',
  animation: `${float} 7s ease-in-out infinite`,
  animationDelay: '1s',
  zIndex: 20,
  '@media (max-width: 768px)': {
    right: '10px',
    bottom: '80px',
    padding: '16px 24px'
  }
});

const ExpNumber = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: '42px',
  fontWeight: 400,
  color: '#d4af37',
  lineHeight: 1,
  marginBottom: '4px'
});

const ExpText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.7)',
  textTransform: 'uppercase',
  letterSpacing: '1px'
});

const BottomStats = styled(Box)({
  display: 'flex',
  gap: '48px',
  marginTop: '56px',
  paddingTop: '40px',
  borderTop: '1px solid rgba(255, 255, 255, 0.08)',
  '@media (max-width: 1200px)': {
    justifyContent: 'center'
  },
  '@media (max-width: 480px)': {
    gap: '24px',
    flexWrap: 'wrap'
  }
});

const StatItem = styled(Box)({
  '@media (max-width: 1200px)': {
    textAlign: 'center'
  }
});

const StatValue = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: '36px',
  fontWeight: 400,
  color: '#ffffff',
  lineHeight: 1,
  marginBottom: '6px',
  '& span': {
    color: '#d4af37'
  }
});

const StatLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.5)'
});

const ScrollIndicator = styled(Box)({
  position: 'absolute',
  bottom: '40px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
  opacity: 0.5,
  transition: 'opacity 0.3s ease',
  '&:hover': {
    opacity: 1
  },
  '@media (max-width: 768px)': {
    display: 'none'
  }
});

const ScrollMouse = styled(Box)({
  width: '26px',
  height: '42px',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '13px',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '8px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '4px',
    height: '8px',
    background: '#d4af37',
    borderRadius: '2px',
    animation: `${fadeInUp} 1.5s ease-in-out infinite`
  }
});

const ScrollText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '11px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.5)',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const HeroSection = ({ businessInfo, heroImage, galleryImages }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToContent = () => {
    const aboutSection = document.querySelector('#about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const defaultImages = [
    'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=600&q=80',
    'https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=600&q=80'
  ];

  const images = galleryImages?.length >= 3 ? galleryImages : defaultImages;

  return (
    <HeroContainer id="home">
      <BackgroundPattern />
      <GoldGlow />
      <PurpleGlow />

      <ContentWrapper>
        <TextContent>
          <TopLabel>
            <LabelLine />
            <LabelText>Welcome</LabelText>
          </TopLabel>

          <HeroTitle>
            {businessInfo?.name ? (
              <>
                Discover<br />
                <GradientText>{businessInfo.name}</GradientText>
              </>
            ) : (
              <>
                Excellence in<br />
                <GradientText>Every Detail</GradientText>
              </>
            )}
          </HeroTitle>

          <HeroSubtitle>
            {businessInfo?.tagline ||
              'We deliver exceptional experiences that exceed expectations. Discover the difference of working with industry leaders.'}
          </HeroSubtitle>

          <FeatureList>
            <FeatureItem>
              <CheckCircleIcon />
              <FeatureText>Premium Quality</FeatureText>
            </FeatureItem>
            <FeatureItem>
              <CheckCircleIcon />
              <FeatureText>Expert Team</FeatureText>
            </FeatureItem>
            <FeatureItem>
              <CheckCircleIcon />
              <FeatureText>24/7 Support</FeatureText>
            </FeatureItem>
          </FeatureList>

          <ButtonGroup>
            <PrimaryButton onClick={() => document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' })}>
              <PrimaryButtonText>Explore Services</PrimaryButtonText>
              <ButtonArrow className="btn-arrow" />
            </PrimaryButton>

            <SecondaryButton onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}>
              <PhoneIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.7)', transition: 'color 0.3s' }} />
              <SecondaryButtonText>Contact Us</SecondaryButtonText>
            </SecondaryButton>
          </ButtonGroup>

          <BottomStats>
            <StatItem>
              <StatValue>500<span>+</span></StatValue>
              <StatLabel>Happy Clients</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>15<span>+</span></StatValue>
              <StatLabel>Years Experience</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>98<span>%</span></StatValue>
              <StatLabel>Satisfaction Rate</StatLabel>
            </StatItem>
          </BottomStats>
        </TextContent>

        <ImageSection>
          <ImageGrid>
            <MainImage>
              <img
                src={heroImage || images[0]}
                alt={businessInfo?.name || 'Hero'}
              />
            </MainImage>
            <SmallImage>
              <img
                src={images[1]}
                alt="Gallery 1"
              />
            </SmallImage>
            <SmallImage>
              <img
                src={images[2]}
                alt="Gallery 2"
              />
            </SmallImage>
          </ImageGrid>

          <RatingCard>
            <RatingStars>
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} />
              ))}
            </RatingStars>
            <RatingText>4.9 out of 5</RatingText>
            <RatingSubtext>Based on 500+ reviews</RatingSubtext>
          </RatingCard>

          <ExperienceCard>
            <ExpNumber>15+</ExpNumber>
            <ExpText>Years of Excellence</ExpText>
          </ExperienceCard>
        </ImageSection>
      </ContentWrapper>

      <ScrollIndicator onClick={scrollToContent}>
        <ScrollMouse />
        <ScrollText>Scroll</ScrollText>
      </ScrollIndicator>
    </HeroContainer>
  );
};

export default HeroSection;
